import React from "react";
import CloseIcon from "@mui/icons-material/Close";

export type ChipVariant = "default" | "success" | "warning" | "error" | "info";
export type ChipSize = "sm" | "md";

interface ChipProps {
  label: React.ReactNode;
  variant?: ChipVariant;
  size?: ChipSize;
  dot?: boolean;
  /**
   * Override the dot color. Use when the dot encodes data (e.g., an asset
   * palette entry) rather than a semantic state. The active-state border
   * still follows `variant` so accessibility cues stay consistent.
   */
  dotColor?: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
  onClose?: () => void;
  ariaLabel?: string;
  className?: string;
}

const ACCENT: Record<ChipVariant, string> = {
  default: "var(--color-fg-muted)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  error: "var(--color-error)",
  info: "var(--color-accent)",
};

const SIZE_PADDING: Record<ChipSize, string> = {
  sm: "var(--size-border-hairline) var(--spacing-sm)",
  md: "var(--spacing-2xs) var(--spacing-sm)",
};
const SIZE_FONT: Record<ChipSize, string> = {
  sm: "var(--font-size-small)",
  md: "var(--font-size)",
};

/**
 * Interactive pill — for filters, selectable tags, removable items.
 *
 * For static status labels (non-interactive), use Badge.
 */
export const Chip: React.FC<ChipProps> = ({
  label,
  variant = "default",
  size = "md",
  dot,
  dotColor,
  count,
  active,
  onClick,
  onClose,
  ariaLabel,
  className,
}) => {
  const accent = ACCENT[variant];
  const dotFill = dotColor ?? accent;
  const isInteractive = onClick !== undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={ariaLabel}
      disabled={!isInteractive && !onClose}
      className={`${isInteractive && !active ? "hover:bg-bg-hover" : ""} ${className ?? ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--spacing-xs)",
        padding: SIZE_PADDING[size],
        fontSize: SIZE_FONT[size],
        fontFamily: "inherit",
        color: "inherit",
        background: active ? "var(--color-bg-subtle)" : undefined,
        border: `var(--size-border-hairline) solid ${active ? accent : "var(--color-border)"}`,
        borderRadius: "var(--radius-app)",
        cursor: isInteractive || onClose ? "pointer" : "default",
        whiteSpace: "nowrap",
        // Explicit line-height + min-height so the chip's outer box is
        // deterministic and survives parent flex/overflow pressure. Without
        // this, `line-height: normal` (browser default 1.2-1.5) varies per
        // font and inner content height is unpredictable, which lets the
        // chip render shorter than its declared padding implies and lets
        // borders get clipped against scrolling parents.
        lineHeight: 1.2,
        minHeight:
          size === "sm"
            ? "var(--control-height-sm)"
            : "calc(var(--control-height-sm) + var(--spacing-2xs))",
        boxSizing: "border-box",
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{
            width: "var(--size-dot-sm)",
            height: "var(--size-dot-sm)",
            borderRadius: "50%",
            background: dotFill,
            flexShrink: 0,
          }}
        />
      )}
      <span className="inline-flex items-center">{label}</span>
      {count !== undefined && (
        <span style={{ color: "var(--color-fg-muted)" }}>{count}</span>
      )}
      {onClose && (
        <span
          role="button"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="text-fg-muted hover:text-fg"
          style={{
            marginInlineStart: "var(--spacing-2xs)",
            cursor: "pointer",
            display: "inline-flex",
          }}
        >
          <CloseIcon className="icon-xs" />
        </span>
      )}
    </button>
  );
};
